import type { FC } from 'react';
import { Capacitor } from '@capacitor/core';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Provider as ReduxProvider } from 'react-redux';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { gtmConfig } from './config';
import { ApiKeyProvider } from './contexts/auth/api-key-context';
import { AUTH_PROVIDER_KEY } from './contexts/auth/auth-context';
import { FirebaseProvider } from './contexts/auth/firebase-context';
import { KeycloakProvider } from './contexts/auth/keycloak-context';
import { OidcProvider } from './contexts/auth/oidc-context';
import { CloudTokenProvider } from './contexts/cloud-token-context';
import { MeProvider } from './contexts/me-context';
import { NativeNotificationsProvider } from './contexts/notifications/native-notifications-context';
import { WebNotificationsProvider } from './contexts/notifications/web-notifications-context';
import { SettingsProvider } from './contexts/settings-context';
import { TenantProvider } from './contexts/tenant-context';
import { useAnalytics } from './hooks/use-analytics';
import { routes } from './routes';
import { MAINTENANCE_KEY, maintenanceRoutes } from './routes/maintenance';
import { store } from './store';
import 'react-quill-new/dist/quill.core.css';
import 'react-quill-new/dist/quill.snow.css';
import 'simplebar-react/dist/simplebar.min.css';
import 'react-toastify/dist/ReactToastify.css';
import { Theme } from './theme/mui';

// TODO: maintenance mode from backend
const enableMaintenanceMode = false;
const maintenanceMode =
  enableMaintenanceMode && !localStorage.getItem(MAINTENANCE_KEY);

const getAuthProvider = () => {
  const authProvider =
    localStorage.getItem(AUTH_PROVIDER_KEY) ||
    globalThis.__TENANT__?.authProvider;

  console.info(`[app] Using auth provider: ${authProvider}`);
  if (authProvider === 'keycloak') return KeycloakProvider;
  if (authProvider === 'firebase') return FirebaseProvider;
  if (authProvider === 'oidc') return OidcProvider;
  if (authProvider === 'api-key') return ApiKeyProvider;
  throw new Error('Invalid auth provider');
};

// select the correct providers
const NotificationsProvider = Capacitor.isNativePlatform()
  ? NativeNotificationsProvider
  : WebNotificationsProvider;
const AuthProvider = getAuthProvider();

const router = createBrowserRouter(
  maintenanceMode ? maintenanceRoutes : routes
);

/**
 * The main app component
 *
 * The component loads the global context providers.
 * Each provider should internally show the splash screen until it is ready to proceed.
 *
 * The order in which the providers are loaded is important.
 */
export const App: FC = () => {
  useAnalytics(gtmConfig);

  return (
    <ReduxProvider store={store}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <TenantProvider>
          <CloudTokenProvider>
            <NotificationsProvider>
              <AuthProvider>
                <MeProvider>
                  <SettingsProvider>
                    <Theme>
                      <RouterProvider router={router} />
                    </Theme>
                  </SettingsProvider>
                </MeProvider>
              </AuthProvider>
            </NotificationsProvider>
          </CloudTokenProvider>
        </TenantProvider>
      </LocalizationProvider>
    </ReduxProvider>
  );
};
